import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  createAppealContribution,
  fetchAppealTypes,
  fetchDeceasedList,
  fetchWillingnessOptions,
  formatSapDate,
  getLoggedInEmpId,
} from "./appealContribution.service";

const CreateAppealContribution: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;

  const [appealOptions, setAppealOptions] = useState<{ label: string; value: string }[]>([]);
  const [willingnessOptions, setWillingnessOptions] = useState<string[]>([]);
  const [deceasedList, setDeceasedList] = useState<any[]>([]);
  const [deceasedAmounts, setDeceasedAmounts] = useState<string[]>([]);
  const [totalAmountContribute, setTotalAmountContribute] = useState("0.00");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [selectedAppealLabel, setSelectedAppealLabel] = useState("");
  const [selectedAppealValue, setSelectedAppealValue] = useState("");
  const [willingness, setWillingness] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    const loadMasters = async () => {
      setLoading(true);
      try {
        const [types, willingnessList, deceased] = await Promise.all([
          fetchAppealTypes(),
          fetchWillingnessOptions(),
          fetchDeceasedList(),
        ]);
        setAppealOptions(types);
        setWillingnessOptions(willingnessList);
        setDeceasedList(deceased);
        setDeceasedAmounts(deceased.map(() => "0.00"));
        const sum = deceased.reduce((acc: number, item: any) => {
          const amt = parseFloat(item?.Amount ?? item?.amount ?? "0");
          return acc + (isNaN(amt) ? 0 : amt);
        }, 0);
        setTotalAmountContribute(sum.toFixed(2));
      } catch (err: any) {
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_appeal_types"));
      } finally {
        setLoading(false);
      }
    };
    loadMasters();
  }, [t]);

  const computedTotal = useMemo(
    () =>
      deceasedAmounts
        .reduce((acc, amount) => {
          const value = parseFloat(amount);
          return acc + (isNaN(value) ? 0 : value);
        }, 0)
        .toFixed(2),
    [deceasedAmounts]
  );

  const needsManualAmount =
    parseInt(selectedAppealValue, 10) >= 1 &&
    willingness.toUpperCase() === "YES";

  const canSubmit =
    isChecked &&
    !!selectedAppealLabel &&
    !!willingness &&
    !submitting &&
    (!needsManualAmount || (!!manualAmount && parseFloat(manualAmount) > 0));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const empId = getLoggedInEmpId();
    if (!empId) {
      toast.error(t("employee_id_not_found"));
      return;
    }

    setSubmitting(true);
    try {
      const isYes = willingness.toUpperCase() === "YES";
      const isNo = willingness.toUpperCase() === "NO";
      const appealIdx = parseInt(selectedAppealValue, 10);
      const firstDeceased = deceasedList[0];
      const firstDeceasedEmpNo =
        firstDeceased?.DeceasedEmployeeNumber ??
        firstDeceased?.deceasedEmployeeNumber ??
        firstDeceased?.EmpNo ??
        firstDeceased?.empNo ??
        "";

      const payload: any = {
        action: "C",
        empId,
        typeOfAppeal: selectedAppealValue,
        willingness: willingness.toUpperCase(),
        willingnessCheckOrNot: isChecked ? "X" : "",
      };

      if (appealIdx === 0 && isYes && deceasedList.length > 0) {
        payload.ToDeceased = {
          results: deceasedList.map((item, idx) => ({
            ReferenceNumber: item?.ReferenceNumber ?? item?.referenceNumber ?? "",
            DeceasedEmployeeNumber:
              item?.DeceasedEmployeeNumber ??
              item?.deceasedEmployeeNumber ??
              item?.EmpNo ??
              "",
            Amount: deceasedAmounts[idx] ?? "0.00",
          })),
        };
        payload.totalAmountContribute = computedTotal;
      } else if (appealIdx === 0 && isNo) {
        payload.totalAmountContribute = totalAmountContribute;
      } else if ((appealIdx === 1 || appealIdx === 2) && isYes) {
        payload.deceasedEmployee = firstDeceasedEmpNo;
        payload.totalAmountContribute = manualAmount;
      } else if ((appealIdx === 1 || appealIdx === 2) && isNo) {
        payload.deceasedEmployee = firstDeceasedEmpNo;
        payload.totalAmountContribute = "0.00";
      } else if (appealIdx >= 3 && isYes) {
        payload.totalAmountContribute = manualAmount;
      } else if (appealIdx >= 3 && isNo) {
        payload.totalAmountContribute = "0.00";
      }

      const res: any = await createAppealContribution(payload);
      toast.success(res?.message || t("thank_you_application_submitted_successfully"));
      navigate(routes.appealContribution.list);
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || t("submission_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{t("create_new_request")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.appealContribution.list}>{t("contribution_on_appeal")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("create_new_request")}
                </li>
              </ol>
            </nav>
          </div>
          <Link to={routes.appealContribution.list} className="btn btn-light">
            <i className="ti ti-arrow-left me-1"></i>
            {t("close")}
          </Link>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white">
            <h5 className="mb-0 fw-bold">{t("contribution_on_appeal")}</h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" role="status" />
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    {t("type_of_appeal_required")}
                  </label>
                  <select
                    className="form-select"
                    value={selectedAppealValue}
                    onChange={(e) => {
                      const opt = appealOptions.find((o) => o.value === e.target.value);
                      setSelectedAppealValue(e.target.value);
                      setSelectedAppealLabel(opt?.label || "");
                      setWillingness("");
                      setManualAmount("");
                      setIsChecked(false);
                    }}
                    required
                  >
                    <option value="">{t("select_appeal_type")}</option>
                    {appealOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {appealOptions.length === 0 && (
                    <div className="form-text text-muted">{t("no_options_available")}</div>
                  )}
                </div>

                {!!selectedAppealLabel && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      {t("willingness_required")}
                    </label>
                    <select
                      className="form-select"
                      value={willingness}
                      onChange={(e) => {
                        setWillingness(e.target.value);
                        setManualAmount("");
                        setIsChecked(false);
                      }}
                      required
                    >
                      <option value="">{t("select_willingness")}</option>
                      {willingnessOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedAppealValue === "0" && willingness.toUpperCase() === "YES" && (
                  <div className="mb-4">
                    <p className="text-muted fst-italic">
                      {t("enter_contribution_amount_instruction")}
                    </p>
                    {deceasedList.length === 0 ? (
                      <div className="alert alert-light border">
                        {t("no_deceased_employees_found")}
                      </div>
                    ) : (
                      deceasedList.map((item, index) => (
                        <div
                          key={item?.SequenceNumber ?? item?.EmpNo ?? index}
                          className="appeal-deceased-card"
                        >
                          <div className="d-flex gap-2 mb-2 flex-wrap align-items-center">
                            <span className="fw-bold text-danger">
                              {item?.DeceasedEmployeeNumber ??
                                item?.deceasedEmployeeNumber ??
                                item?.EmpNo ??
                                "-"}
                            </span>
                            <span className="fw-semibold text-dark">
                              {item?.DeceasedEmployeeName ??
                                item?.deceasedEmployeeName ??
                                item?.EmpName ??
                                "-"}
                            </span>
                          </div>
                          <div className="appeal-info-row">
                            <span className="appeal-info-label">{t("designation")}:</span>
                            <span className="appeal-info-value">
                              {item?.Designation ?? "-"}
                            </span>
                          </div>
                          <div className="appeal-info-row">
                            <span className="appeal-info-label">{t("department")}:</span>
                            <span className="appeal-info-value">
                              {item?.Department ?? "-"}
                            </span>
                          </div>
                          <div className="row g-2 mb-2">
                            <div className="col-md-6">
                              <div className="appeal-info-row">
                                <span className="appeal-info-label">{t("start_date")}:</span>
                                <span className="appeal-info-value">
                                  {formatSapDate(item?.AppealStartDate ?? item?.StartDate)}
                                </span>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="appeal-info-row">
                                <span className="appeal-info-label">{t("end_date")}:</span>
                                <span className="appeal-info-value text-danger fw-semibold">
                                  {formatSapDate(item?.AppealEndDate ?? item?.EndDate)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="appeal-amount-row">
                            <label className="appeal-amount-label mb-0">
                              * {t("amount_inr")}
                            </label>
                            <input
                              type="text"
                              className="form-control appeal-amount-input"
                              value={deceasedAmounts[index] ?? "0.00"}
                              onChange={(e) => {
                                const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                                setDeceasedAmounts((prev) => {
                                  const next = [...prev];
                                  next[index] = cleaned;
                                  return next;
                                });
                              }}
                              onBlur={() => {
                                setDeceasedAmounts((prev) => {
                                  const next = [...prev];
                                  const num = parseFloat(next[index] ?? "");
                                  next[index] = isNaN(num) ? "0.00" : num.toFixed(2);
                                  return next;
                                });
                              }}
                            />
                          </div>
                        </div>
                      ))
                    )}
                    <div className="appeal-total-bar">
                      <span className="appeal-total-label">
                        {t("total_amount_to_be_contributed")}:
                      </span>
                      <span className="appeal-total-value">₹ {computedTotal}</span>
                    </div>
                  </div>
                )}

                  {!!selectedAppealLabel &&
                  !!willingness &&
                  parseInt(selectedAppealValue, 10) >= 1 &&
                  willingness.toUpperCase() === "YES" && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        {t("amount_inr_required")}
                      </label>
                      <input
                        type="text"
                        className="form-control appeal-amount-input"
                        value={manualAmount}
                        placeholder={t("enter_amount")}
                        onChange={(e) =>
                          setManualAmount(e.target.value.replace(/[^0-9.]/g, ""))
                        }
                      />
                      {manualAmount && (
                        <div className="appeal-total-bar mt-3">
                          <span className="appeal-total-label">
                            {t("total_amount_to_be_contributed")}:
                          </span>
                          <span className="appeal-total-value">
                            ₹ {isNaN(parseFloat(manualAmount)) ? "0.00" : parseFloat(manualAmount).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                {!!selectedAppealLabel && !!willingness && (
                  <>
                    <div className="form-check mb-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="willingnessCheck"
                        checked={isChecked}
                        onChange={(e) => setIsChecked(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="willingnessCheck">
                        {t("willingness_confirmation")}
                      </label>
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                      <Link to={routes.appealContribution.list} className="btn btn-light">
                        {t("close")}
                      </Link>
                      <button type="submit" className="btn btn-danger" disabled={!canSubmit}>
                        {submitting && (
                          <span className="spinner-border spinner-border-sm me-2" />
                        )}
                        {t("submit")}
                      </button>
                    </div>
                  </>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
      <style>{`
        .appeal-deceased-card {
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 14px;
          margin-bottom: 14px;
          background: #fff;
        }
        .appeal-info-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
        }
        .appeal-info-label {
          font-weight: 700;
          min-width: 110px;
          color: #111827;
          font-size: 13px;
        }
        .appeal-info-value {
          flex: 1;
          color: #111827;
          font-size: 13px;
        }
        .appeal-amount-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-top: 8px;
        }
        .appeal-amount-label {
          font-weight: 700;
          color: #111827;
          flex: 1;
        }
        .appeal-amount-input {
          max-width: 160px;
          text-align: right;
          font-weight: 700;
          border: 1.5px solid var(--primary, #dc2626);
        }
        .appeal-total-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          background: rgba(220, 38, 38, 0.10);
          border: 1.5px solid var(--primary, #dc2626);
          border-radius: 8px;
          padding: 14px 18px;
          margin-top: 8px;
        }
        .appeal-total-label {
          font-weight: 700;
          font-size: 15px;
          color: #111827;
          flex: 1 1 200px;
        }
        .appeal-total-value {
          font-weight: 800;
          font-size: 22px;
          line-height: 1.2;
          color: #dc2626;
          white-space: nowrap;
          flex: 0 0 auto;
        }
      `}</style>
    </div>
  );
};

export default CreateAppealContribution;
